// ── #623 map auto-fit ───────────────────────────────────────────────────────
//
// Pure geometry helper: given the [lon, lat] centroids of the countries a brand
// ships to, work out the ZoomableGroup { center, zoom } that frames them all.
//
// Why a separate module (and why it takes plain [lon, lat] points, not
// geographies): ShippingTab.js pulls in react-simple-maps and world-atlas, both
// ESM that CRA's Jest refuses to transform. Centroid extraction (d3-geo's
// geoCentroid, also ESM) therefore happens in the COMPONENT; this module does
// nothing but arithmetic on the resulting lon/lat points, so it imports nothing
// and is trivially unit-testable with mock coordinates.
//
// The zoom model matches react-simple-maps v5's ZoomableGroup: zoom=1 is the
// whole projected world, and doubling the zoom halves the visible span. We size
// the zoom so the covered countries' lon/lat span (plus padding) fills the map,
// then clamp to [MIN_ZOOM, MAX_ZOOM].

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 8;

// The full geographic span the base projection shows, in degrees. Longitude
// wraps the globe (360°); latitude is compressed by the geoEqualEarth/Mercator
// framing, so ~180° of usable vertical extent is a safe divisor.
const WORLD_LON_SPAN = 360;
const WORLD_LAT_SPAN = 180;

// Leave a margin around the covered countries so they don't touch the edges,
// and so a single country still shows some surrounding context rather than
// slamming to MAX_ZOOM.
const PADDING = 1.6;

// A single point has zero span; frame it at a comfortable regional zoom rather
// than the maximum, so one country doesn't fill the whole viewport.
const SINGLE_POINT_ZOOM = 4;

const WORLD_VIEW = { center: [0, 0], zoom: MIN_ZOOM };

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * Compute the ZoomableGroup { center: [lon, lat], zoom } that frames a set of
 * country centroids.
 *
 * @param {Array<[number, number]>} points  [lon, lat] centroids of covered countries.
 * @param {Object} [opts]
 * @param {boolean} [opts.restOfWorld]  When true the whole world is covered → world view.
 * @returns {{ center: [number, number], zoom: number }}
 */
export function computeMapFit(points, opts = {}) {
  const { restOfWorld = false } = opts;

  // Whole world covered, or nothing to frame → the default world view.
  if (restOfWorld) return WORLD_VIEW;

  const valid = (points || []).filter(
    (p) =>
      Array.isArray(p) &&
      Number.isFinite(p[0]) &&
      Number.isFinite(p[1])
  );
  if (valid.length === 0) return WORLD_VIEW;

  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (const [lon, lat] of valid) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  const center = [(minLon + maxLon) / 2, (minLat + maxLat) / 2];

  // A single country (or several that share a centroid): no span to fit.
  const lonSpan = maxLon - minLon;
  const latSpan = maxLat - minLat;
  if (lonSpan === 0 && latSpan === 0) {
    return { center, zoom: SINGLE_POINT_ZOOM };
  }

  // Zoom that would exactly fit each axis, then take the tighter (smaller) one
  // so BOTH axes fit, with padding so the countries don't touch the edges.
  const lonZoom = WORLD_LON_SPAN / (lonSpan * PADDING);
  const latZoom = WORLD_LAT_SPAN / (latSpan * PADDING);
  // Guard the degenerate single-axis case (span 0 on one axis).
  const candidates = [lonZoom, latZoom].filter((z) => Number.isFinite(z) && z > 0);
  const fitZoom = candidates.length ? Math.min(...candidates) : SINGLE_POINT_ZOOM;

  return { center, zoom: clamp(fitZoom, MIN_ZOOM, MAX_ZOOM) };
}
